"use client";

import React, { useEffect, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { useReviews } from "@/hooks/useReviews";
import { ReviewCard } from "@/components/reviews/ReviewCard";
import { Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
export default function ShareExperience() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    reviews,
    loading,
    error,
    refetch
  } = useReviews();
  useEffect(() => {
    let userScrolled = false;
    const timers: number[] = [];
    const handleUserInteraction = () => {
      userScrolled = true;
    };
    window.addEventListener("wheel", handleUserInteraction, {
      passive: true
    });
    window.addEventListener("touchmove", handleUserInteraction, {
      passive: true
    });
    window.addEventListener("keydown", e => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Space"].includes(e.code)) {
        userScrolled = true;
      }
    });
    const performScroll = () => {
      if (userScrolled) return;
      if (typeof window === "undefined") return;
      if (window.location.hash !== "#shareyourexperience") return;
      const target = sectionRef.current || document.getElementById("shareyourexperience");
      if (!target) return;
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start"
      });
    };
    const runScheduledScrolls = () => {
      userScrolled = false;
      // Immediate / short delays to handle async images/reviews shifting layout
      performScroll();
      timers.push(window.setTimeout(performScroll, 100));
      timers.push(window.setTimeout(performScroll, 600));
      timers.push(window.setTimeout(performScroll, 1500));
    };
    if (window.location.hash === "#shareyourexperience") {
      runScheduledScrolls();
    }
    const onHashChange = () => {
      if (window.location.hash === "#shareyourexperience") {
        runScheduledScrolls();
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => {
      timers.forEach(t => clearTimeout(t));
      window.removeEventListener("wheel", handleUserInteraction);
      window.removeEventListener("touchmove", handleUserInteraction);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);
  const latestReviews = reviews.slice(0, 6);
  return <section ref={sectionRef} data-section-id="shareyourexperience" id="shareyourexperience" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-t border-border scroll-mt-20">
      <div ref={ref} className={cn("container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl transition-all duration-300", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 motion-reduce:opacity-100 motion-reduce:translate-y-0")}>
        {/* Left-aligned clean heading */}
        <div className="max-w-3xl mb-8 md:mb-12">
          <div className="border-l-4 border-primary pl-3 mb-3">
            <span className="font-mono text-xs uppercase tracking-widest text-primary font-medium">
              Client Feedback
            </span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-normal tracking-tight text-foreground leading-tight mb-3">
            Leave a review
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Tell us about your project with Ablaze Contracting Service LLC.
          </p>
        </div>

        {/* Review Submission Form Card */}
        <div className="max-w-3xl bg-card border border-border rounded p-6 md:p-8">
          <ReviewForm />
        </div>

        {/* Latest Reviews Section */}
        <div className="mt-14 md:mt-20 pt-10 md:pt-14 border-t border-border">
          <div className="max-w-3xl mb-8 md:mb-10">
            <h3 className="font-serif text-xl sm:text-2xl font-normal tracking-tight text-foreground mb-2">
              Latest reviews
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
              Read feedback from property owners we have worked with.
            </p>
          </div>

          {/* Loading state */}
          {loading && reviews.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
              <Loader2 className="size-6 animate-spin text-primary" />
              <p className="font-mono text-xs uppercase tracking-widest">
                Loading latest reviews...
              </p>
            </div>}

          {/* Error state */}
          {error && reviews.length === 0 && <div className="flex flex-col items-center justify-center py-8 px-6 text-center border border-destructive/30 bg-destructive/10 rounded max-w-md">
              <AlertCircle className="size-6 text-destructive mb-2" />
              <p className="text-destructive font-medium text-sm mb-4">{error}</p>
              <button type="button" onClick={() => refetch()} className="text-xs uppercase font-mono tracking-widest text-primary hover:underline cursor-pointer">
                Retry
              </button>
            </div>}

          {/* Empty state */}
          {!loading && !error && latestReviews.length === 0 && <div className="py-10 px-6 border border-dashed border-border rounded bg-card max-w-md">
              <p className="text-muted-foreground text-sm">
                No reviews yet — submit yours above to be the first.
              </p>
            </div>}

          {/* Grid of latest reviews (up to 6) */}
          {latestReviews.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestReviews.map(review => <ReviewCard key={review.id} review={review} />)}
            </div>}
        </div>
      </div>
    </section>;
}